import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Rocket, ArrowLeft, Trophy, Clock, Star, 
  RotateCcw, Crosshair, Shield, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const SpaceMathGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  
  const [gameState, setGameState] = useState('ready');
  const [difficulty, setDifficulty] = useState('medium'); // easy, medium, hard
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [currentEquation, setCurrentEquation] = useState(null);
  const [enemies, setEnemies] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [shipPosition, setShipPosition] = useState(50);
  const [highScore, setHighScore] = useState(0);

  // Difficulty settings
  const difficultySettings = {
    easy: { speed: 0.2, maxNum: 10, lives: 5, operations: ['+'], levelThreshold: 150 },
    medium: { speed: 0.3, maxNum: 20, lives: 3, operations: ['+', '-'], levelThreshold: 100 },
    hard: { speed: 0.5, maxNum: 50, lives: 2, operations: ['+', '-', '×'], levelThreshold: 80 }
  };

  // Generate math equation with multiple choice answers
  const generateEquation = useCallback(() => {
    const settings = difficultySettings[difficulty];
    const operations = settings.operations;
    const op = operations[Math.floor(Math.random() * Math.min(level, operations.length))];
    
    let num1, num2, answer;
    const maxNum = Math.min(level * 2 + settings.maxNum, settings.maxNum * 2);
    
    switch (op) {
      case '+':
        num1 = Math.floor(Math.random() * maxNum) + 1;
        num2 = Math.floor(Math.random() * maxNum) + 1;
        answer = num1 + num2;
        break;
      case '-':
        num1 = Math.floor(Math.random() * maxNum) + 10;
        num2 = Math.floor(Math.random() * Math.min(num1, maxNum)) + 1;
        answer = num1 - num2;
        break;
      case '×':
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
        answer = num1 * num2;
        break;
      default:
        num1 = 1; num2 = 1; answer = 2;
    }

    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const wrong = answer + Math.floor(Math.random() * 20) - 10;
      if (wrong !== answer && wrong > 0 && !wrongAnswers.includes(wrong)) {
        wrongAnswers.push(wrong);
      }
    }

    // Shuffle all answers
    const allAnswers = [answer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    
    return {
      display: `${num1} ${op} ${num2}`,
      answer,
      options: allAnswers,
      points: op === '×' ? 30 : 20
    };
  }, [level, difficulty]);

  // Spawn enemies (answers falling from top)
  const spawnEnemies = useCallback(() => {
    if (!currentEquation) return;
    
    const settings = difficultySettings[difficulty];
    const newEnemies = currentEquation.options.map((opt, idx) => ({
      id: Date.now() + idx,
      value: opt,
      x: 15 + (idx * 23), // Spread across screen
      y: 0,
      speed: settings.speed + (level * 0.1),
      isCorrect: opt === currentEquation.answer
    }));
    
    setEnemies(newEnemies);
  }, [currentEquation, level, difficulty]);

  // Start game
  const startGame = useCallback((selectedDifficulty) => {
    if (selectedDifficulty) setDifficulty(selectedDifficulty);
    const settings = difficultySettings[selectedDifficulty || difficulty];
    setGameState('playing');
    setScore(0);
    setLives(settings.lives);
    setLevel(1);
    setEnemies([]);
    setExplosions([]);
    setShipPosition(50);
    const eq = generateEquation();
    setCurrentEquation(eq);
  }, [generateEquation, difficulty]);

  // Move enemies down
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const interval = setInterval(() => {
      setEnemies(prev => {
        const updated = prev.map(e => ({ ...e, y: e.y + e.speed }));
        
        // Check if any enemy reached bottom
        const escaped = updated.find(e => e.y >= 85);
        if (escaped) {
          if (escaped.isCorrect) {
            // Correct answer escaped - lose life
            setLives(l => {
              const newLives = l - 1;
              if (newLives <= 0) {
                setGameState('finished');
              }
              return newLives;
            });
            toast.error('The correct answer escaped!');
          }
          // Generate new equation
          const newEq = generateEquation();
          setCurrentEquation(newEq);
          return [];
        }
        
        return updated;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [gameState, generateEquation]);

  // Spawn enemies when equation changes
  useEffect(() => {
    if (currentEquation && gameState === 'playing') {
      spawnEnemies();
    }
  }, [currentEquation, gameState, spawnEnemies]);

  // Level up
  useEffect(() => {
    const levelThreshold = level * 100;
    if (score >= levelThreshold && score > 0) {
      setLevel(l => l + 1);
      toast.success(`Level ${level + 1}! Enemies getting faster!`);
    }
  }, [score, level]);

  // Keyboard controls
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setShipPosition(p => Math.max(5, p - 5));
      } else if (e.key === 'ArrowRight') {
        setShipPosition(p => Math.min(95, p + 5));
      } else if (e.key === ' ' || e.key === 'Enter') {
        shootLaser();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, shipPosition, enemies]);

  // Shoot laser
  const shootLaser = useCallback(() => {
    // Find enemy closest to ship's x position
    const shipX = shipPosition;
    let closestEnemy = null;
    let closestDist = Infinity;
    
    enemies.forEach(enemy => {
      const dist = Math.abs(enemy.x - shipX);
      if (dist < closestDist && dist < 15) {
        closestDist = dist;
        closestEnemy = enemy;
      }
    });
    
    if (closestEnemy) {
      // Add explosion effect
      setExplosions(prev => [...prev, { id: Date.now(), x: closestEnemy.x, y: closestEnemy.y }]);
      setTimeout(() => setExplosions(prev => prev.filter(e => e.id !== Date.now())), 300);
      
      if (closestEnemy.isCorrect) {
        // Hit correct answer!
        setScore(s => s + currentEquation.points);
        toast.success(`+${currentEquation.points} points!`);
        const newEq = generateEquation();
        setCurrentEquation(newEq);
        setEnemies([]);
      } else {
        // Hit wrong answer
        setLives(l => {
          const newLives = l - 1;
          if (newLives <= 0) {
            setGameState('finished');
          }
          return newLives;
        });
        toast.error('Wrong answer!');
        setEnemies(prev => prev.filter(e => e.id !== closestEnemy.id));
      }
    }
  }, [enemies, shipPosition, currentEquation, generateEquation]);

  // End game
  const endGame = async () => {
    if (score > highScore) setHighScore(score);
    
    try {
      await api.post('/games/complete', {
        game_id: 'space-math',
        score: score,
        time_seconds: 0,
        metadata: { level, lives_remaining: lives }
      });
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  useEffect(() => {
    if (gameState === 'finished') {
      endGame();
    }
  }, [gameState]);

  return (
    <StudentLayout>
      <div data-testid="space-math-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(difficultySettings[difficulty].lives)].map((_, i) => (
                  <Shield key={i} className={`w-6 h-6 ${i < lives ? 'text-red-500 fill-red-500' : 'text-slate-300'}`} />
                ))}
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-indigo-100 border-indigo-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-indigo-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Space Math Invaders</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Level ${level} • Shoot the correct answer!` : 'Defend Earth with math!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-slate-900 to-indigo-900">
            <div className="w-24 h-24 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Rocket className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Space Math Invaders</h2>
            <p className="text-lg text-indigo-200 mb-6">
              Alien equations are invading! Shoot the correct answer before they reach Earth!
            </p>
            
            {/* Difficulty Selection */}
            <div className="mb-8">
              <p className="text-indigo-300 mb-4">Select Difficulty:</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'easy' 
                      ? 'bg-green-500 text-white scale-105' 
                      : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                  }`}
                >
                  Easy
                  <span className="block text-xs opacity-75">5 lives, + only</span>
                </button>
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'medium' 
                      ? 'bg-amber-500 text-white scale-105' 
                      : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                  }`}
                >
                  Medium
                  <span className="block text-xs opacity-75">3 lives, + -</span>
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'hard' 
                      ? 'bg-red-500 text-white scale-105' 
                      : 'bg-indigo-800 text-indigo-300 hover:bg-indigo-700'
                  }`}
                >
                  Hard
                  <span className="block text-xs opacity-75">2 lives, + - ×</span>
                </button>
              </div>
            </div>
            
            <div className="text-indigo-300 mb-8 space-y-2">
              <p>⬅️ ➡️ Move ship left/right</p>
              <p>SPACE or ENTER to shoot</p>
              <p>Or click on answers!</p>
            </div>
            <Button onClick={() => startGame(difficulty)} className="bg-indigo-500 hover:bg-indigo-600 text-white text-lg px-10 py-6">
              Launch Mission
              <Rocket className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentEquation && (
          <div className="student-card bg-gradient-to-b from-slate-900 to-indigo-900 min-h-[400px] relative overflow-hidden">
            {/* Stars background */}
            <div className="absolute inset-0">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
            
            {/* Equation Display */}
            <div className="text-center pt-4 relative z-10">
              <div className="text-4xl font-heading font-bold text-white mb-2">
                {currentEquation.display} = ?
              </div>
              <p className="text-indigo-300 text-sm">Shoot the correct answer!</p>
            </div>
            
            {/* Falling Enemies (Answers) */}
            <div className="relative h-64 mt-4">
              {enemies.map(enemy => (
                <button
                  key={enemy.id}
                  onClick={() => {
                    if (enemy.isCorrect) {
                      setScore(s => s + currentEquation.points);
                      toast.success(`+${currentEquation.points} points!`);
                      const newEq = generateEquation();
                      setCurrentEquation(newEq);
                      setEnemies([]);
                    } else {
                      setLives(l => {
                        const newLives = l - 1;
                        if (newLives <= 0) setGameState('finished');
                        return newLives;
                      });
                      toast.error('Wrong!');
                      setEnemies(prev => prev.filter(e => e.id !== enemy.id));
                    }
                  }}
                  className="absolute transform -translate-x-1/2 transition-all duration-75 hover:scale-110"
                  style={{
                    left: `${enemy.x}%`,
                    top: `${enemy.y}%`
                  }}
                >
                  <div className="w-16 h-16 bg-red-500 rounded-lg border-2 border-red-300 flex items-center justify-center shadow-lg shadow-red-500/50 hover:bg-red-400">
                    <span className="text-white font-bold text-xl">{enemy.value}</span>
                  </div>
                </button>
              ))}
              
              {/* Explosions */}
              {explosions.map(exp => (
                <div
                  key={exp.id}
                  className="absolute w-20 h-20 animate-ping"
                  style={{ left: `${exp.x}%`, top: `${exp.y}%` }}
                >
                  <div className="w-full h-full bg-yellow-500 rounded-full opacity-75" />
                </div>
              ))}
            </div>
            
            {/* Ship */}
            <div className="relative h-16 mt-4">
              <div
                className="absolute bottom-0 transform -translate-x-1/2 transition-all duration-100"
                style={{ left: `${shipPosition}%` }}
              >
                <div className="w-16 h-12 relative">
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-12 border-l-transparent border-r-transparent border-b-cyan-400" />
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-6 bg-cyan-500 rounded-t-lg" />
                  <Crosshair className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-4 h-4 text-cyan-300" />
                </div>
              </div>
            </div>
            
            {/* Controls hint */}
            <div className="text-center text-indigo-400 text-sm mt-2">
              Use ⬅️ ➡️ arrows and SPACE to shoot • Or click answers directly
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-slate-900 to-indigo-900">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Mission Complete!</h2>
            <p className="text-xl text-indigo-200 mb-4">
              You reached Level {level}!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-400">+{score}</span>
              <span className="text-indigo-200 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => startGame()} className="border-indigo-400 text-indigo-200 hover:bg-indigo-800">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-indigo-500 hover:bg-indigo-600">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default SpaceMathGame;
